import { useState, useEffect, useRef, type CSSProperties } from "react";
import type { EntityTypeDefinition, FieldDefinition, FieldType } from "../../../lib/types";
import { useUpdateEntityType } from "../../../lib/hooks/use-kuvio";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../lib/query-keys";

interface AddColumnModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: EntityTypeDefinition;
  onAdded?: () => void;
}

const RESERVED_NAMES = ["id", "_meta", "created_at", "updated_at"];

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & time" },
  { value: "enum", label: "Enum" },
  { value: "reference", label: "Reference" },
];

export function AddColumnModal({ open, onOpenChange, entityType, onAdded }: AddColumnModalProps) {
  const [name, setName] = useState("");
  const [fieldType, setFieldType] = useState<FieldType>("text");
  const [choices, setChoices] = useState("");
  const [target, setTarget] = useState("");
  const [required, setRequired] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const updateEntityType = useUpdateEntityType();
  const qc = useQueryClient();

  const allEntityTypes = (qc.getQueryData<EntityTypeDefinition[]>(queryKeys.entityTypes()) ?? [])
    .filter((t) => t.slug !== entityType.slug);

  useEffect(() => {
    if (open) {
      setName("");
      setFieldType("text");
      setChoices("");
      setTarget(allEntityTypes[0]?.slug ?? "");
      setRequired(false);
      setError("");
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const slug = name.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  const existingNames = entityType.fields.map((f) => f.name);

  function validate(): string | null {
    const trimmed = name.trim();
    if (!trimmed) return "Name is required.";
    if (trimmed.length > 40) return "Name must be 40 characters or fewer.";
    if (/\s/.test(slug) || !slug) return "Name must contain at least one letter or number.";
    if (existingNames.includes(slug)) return "A column with this name already exists.";
    if (RESERVED_NAMES.includes(slug)) return `Use a different name — '${slug}' is reserved.`;
    if (fieldType === "enum") {
      const parsed = choices.split(",").map((c) => c.trim()).filter(Boolean);
      if (parsed.length < 2) return "Enum fields need at least 2 choices.";
    }
    if (fieldType === "reference" && !target) return "Select a target entity type.";
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");

    const newField: FieldDefinition = {
      name: slug,
      type: fieldType,
      ...(required ? { required: true } : {}),
      ...(fieldType === "enum" ? { choices: choices.split(",").map((c) => c.trim()).filter(Boolean) } : {}),
      ...(fieldType === "reference" ? { target } : {}),
    };

    updateEntityType.mutate(
      {
        slug: entityType.slug,
        patch: { fields: [...entityType.fields, newField] },
        expectedVersion: entityType._version,
      },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: queryKeys.entities(entityType.slug) });
          onAdded?.();
          onOpenChange(false);
        },
        onError: (err: Error) => {
          const msg = err.message;
          if (msg.includes("name_exists") || msg.includes("already exists")) {
            setError("A column with this name already exists.");
          } else if (msg.includes("name_reserved") || msg.includes("reserved")) {
            setError(`Use a different name — '${slug}' is reserved.`);
          } else if (msg.includes("version_conflict") || msg.includes("version")) {
            setError("Schema changed elsewhere — reload and try again.");
          } else {
            setError(msg || "Failed to add column.");
          }
        },
      },
    );
  }

  function handleClose() {
    if (!updateEntityType.isPending) {
      onOpenChange(false);
    }
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };

  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 420,
    maxWidth: "90vw",
  };

  const fieldStyle: CSSProperties = { marginBottom: 16 };

  const labelStyle: CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    marginBottom: 4,
  };

  const inputStyle: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    boxSizing: "border-box",
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div
        style={modal}
        role="dialog"
        aria-label="Add column"
        onKeyDown={(e) => { if (e.key === "Escape") handleClose(); }}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
            Add column
          </div>
          <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginTop: 2 }}>
            Add a new field to {entityType.name}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Name</label>
            <input
              ref={nameRef}
              type="text"
              style={inputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Notes"
              maxLength={40}
            />
            {error && (
              <div style={{ fontSize: 12, color: "var(--c-red-600)", marginTop: 4 }}>
                {error}
              </div>
            )}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Type</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {FIELD_TYPES.map((ft) => (
                <label
                  key={ft.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 8px",
                    borderRadius: "var(--border-radius-sm)",
                    background: fieldType === ft.value ? "var(--color-background-secondary)" : "transparent",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="radio"
                    name="field-type"
                    value={ft.value}
                    checked={fieldType === ft.value}
                    onChange={() => setFieldType(ft.value)}
                    style={{ flexShrink: 0 }}
                  />
                  {ft.label}
                </label>
              ))}
            </div>
          </div>

          {fieldType === "enum" && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Choices (comma-separated)</label>
              <input
                type="text"
                style={inputStyle}
                value={choices}
                onChange={(e) => setChoices(e.target.value)}
                placeholder="e.g. low, medium, high"
              />
            </div>
          )}

          {fieldType === "reference" && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Target entity type</label>
              <select
                style={{ ...inputStyle, cursor: "pointer" }}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                {allEntityTypes.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.name}</option>
                ))}
                {allEntityTypes.length === 0 && (
                  <option value="" disabled>No other entity types</option>
                )}
              </select>
            </div>
          )}

          <div style={{ ...fieldStyle, display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              id="add-col-required"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            <label htmlFor="add-col-required" style={{ fontSize: 13, cursor: "pointer" }}>
              Required
            </label>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button
              type="button"
              onClick={handleClose}
              style={{ fontSize: 13, padding: "8px 14px" }}
              disabled={updateEntityType.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!name.trim() || updateEntityType.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {updateEntityType.isPending ? "Adding..." : "Add column"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
